import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Pencil, Printer, Ban, Tag, AlertTriangle, RefreshCw, ListChecks } from "lucide-react";
import api, { formatApiError, downloadFromApi } from "../lib/api";
import BulkCreateRpModal from "./BulkCreateRpModal";

// One-stop operational control center for every pallet on an order.
// Status, qty edit, per-pallet reprint, void, LTL freight visibility,
// product / order line attribution — all in one table.

const STATUS_COLORS = {
    reserved: "bg-amber-100 text-amber-800 border-amber-300",
    planned: "bg-sky-100 text-sky-800 border-sky-300",
    produced: "bg-emerald-100 text-emerald-800 border-emerald-300",
    staged: "bg-indigo-100 text-indigo-800 border-indigo-300",
    shipped: "bg-slate-200 text-slate-700 border-slate-400",
    delivered: "bg-violet-100 text-violet-800 border-violet-300",
    voided: "bg-red-100 text-red-800 border-red-300",
    cancelled: "bg-rose-100 text-rose-800 border-rose-300",
};

function StatusPill({ status }) {
    const cls = STATUS_COLORS[status] || "bg-gray-100 text-gray-700 border-gray-300";
    return (
        <span
            className={`inline-block text-[10px] font-semibold uppercase tracking-wider border px-2 py-[2px] rounded-sm ${cls}`}
            data-testid={`pallet-status-${status}`}
        >
            {status}
        </span>
    );
}

function freightSummary(pallet) {
    const f = pallet?.freight;
    if (!f) return "—";
    const { width_in, length_in, height_in, weight_lbs } = f;
    if (!width_in || !length_in || !height_in || !weight_lbs) return "—";
    return `${width_in}×${length_in}×${height_in} · ${weight_lbs} lb`;
}

export default function IssuedLoadTagsPanel({ orderId, isLtl, isAdmin, onChanged }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [busyId, setBusyId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [editReason, setEditReason] = useState("");
    const [voidId, setVoidId] = useState(null);
    const [voidReason, setVoidReason] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);

    const toggleSelected = (id) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const clearSelection = () => setSelectedIds(new Set());

    const load = useCallback(async () => {
        if (!orderId) return;
        try {
            const { data } = await api.get(`/orders/${orderId}/pallets`);
            setRows(data);
            setError("");
        } catch (err) {
            console.error("Issued LT load failed", err);
            setError(formatApiError(err.response?.data?.detail) || err.message);
        }
    }, [orderId]);

    useEffect(() => {
        load();
    }, [load, refreshKey]);

    const refresh = useCallback(() => {
        setRefreshKey((k) => k + 1);
        onChanged?.();
    }, [onChanged]);

    const selectedReservedPallets = useMemo(
        () =>
            (rows || []).filter(
                (p) => selectedIds.has(p.id) && p.status === "reserved",
            ),
        [rows, selectedIds],
    );

    const groups = useMemo(() => {
        const map = new Map();
        for (const p of rows || []) {
            const key = p.order_line_id || "—";
            const list = map.get(key) || [];
            list.push(p);
            map.set(key, list);
        }
        return Array.from(map.entries());
    }, [rows]);

    const startEdit = (p) => {
        setEditingId(p.id);
        setEditValue(String(p.units_per_pallet ?? 0));
        setEditReason("");
        setError("");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditValue("");
        setEditReason("");
    };

    const saveEdit = async (p) => {
        const n = Number(editValue);
        if (!Number.isFinite(n) || n < 0) {
            setError("Units must be a non-negative number.");
            return;
        }
        setBusyId(p.id);
        try {
            await api.patch(`/pallets/${p.id}/units`, {
                units_per_pallet: Math.floor(n),
                reason: editReason || undefined,
            });
            cancelEdit();
            refresh();
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusyId(null);
        }
    };

    const reprint = async (p) => {
        setBusyId(p.id);
        try {
            const { data: doc } = await api.post(`/pallets/${p.id}/regenerate-tag`);
            await downloadFromApi(`/documents/${doc.id}/download`, doc.filename);
            refresh();
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusyId(null);
        }
    };

    const startVoid = (p) => {
        setVoidId(p.id);
        setVoidReason("");
        setError("");
    };

    const cancelVoid = () => {
        setVoidId(null);
        setVoidReason("");
    };

    const submitVoid = async (p) => {
        if (voidReason.trim().length < 3) {
            setError("Void reason must be at least 3 characters.");
            return;
        }
        setBusyId(p.id);
        try {
            await api.post(`/pallets/${p.id}/void`, { reason: voidReason });
            cancelVoid();
            refresh();
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusyId(null);
        }
    };

    if (rows === null) {
        return (
            <div className="text-sm text-gray-500 mb-4">Loading issued pallets…</div>
        );
    }

    return (
        <section className="mb-6" data-testid="issued-load-tags-panel">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-wider font-semibold text-gray-600 flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5" /> Issued Load Tags / Pallets
                    <span className="odr-mono text-[11px] font-normal text-gray-500 ml-2">
                        {rows.length} pallet{rows.length === 1 ? "" : "s"}
                    </span>
                    {selectedReservedPallets.length > 0 && (
                        <span
                            className="odr-mono text-[11px] font-semibold text-red-700 ml-2"
                            data-testid="bulk-rp-selection-count"
                        >
                            {selectedReservedPallets.length} selected
                        </span>
                    )}
                </h3>
                <div className="flex items-center gap-3">
                    {selectedReservedPallets.length > 0 && (
                        <>
                            <button
                                type="button"
                                onClick={() => setBulkOpen(true)}
                                className="text-xs font-semibold bg-red-700 hover:bg-red-800 text-white rounded-sm px-3 py-1.5 inline-flex items-center gap-1.5"
                                data-testid="bulk-rp-open"
                            >
                                <ListChecks className="w-3 h-3" />
                                Create RP From Selected ({selectedReservedPallets.length})
                            </button>
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="text-[11px] text-gray-500 hover:text-gray-800"
                                data-testid="bulk-rp-clear"
                            >
                                Clear
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={refresh}
                        className="text-[11px] text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                        data-testid="issued-lt-refresh"
                    >
                        <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div
                    className="text-xs border border-red-300 bg-red-50 text-red-800 rounded-sm px-3 py-2 mb-3 inline-flex items-start gap-2"
                    data-testid="issued-lt-error"
                >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                    <div>{error}</div>
                </div>
            )}

            {rows.length === 0 && (
                <div className="text-sm text-gray-500 bg-white border border-gray-200 rounded-sm px-4 py-6 text-center">
                    No pallets issued for this order yet. Generate a Blank, Manual,
                    or RP-based Load Tag to reserve PLLs.
                </div>
            )}

            {rows.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="odr-table" data-testid="issued-lt-table">
                        <thead>
                            <tr>
                                <th className="w-8"></th>
                                <th>PLL</th>
                                <th>Status</th>
                                <th>Product</th>
                                <th>RP</th>
                                <th className="text-right">Units</th>
                                {isLtl && <th>Freight (W×L×H · Wt)</th>}
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map(([lineId, list]) =>
                                list.map((p, idx) => (
                                    <tr
                                        key={p.id}
                                        data-testid={`pallet-row-${p.pallet_number}`}
                                        className={
                                            p.status === "voided"
                                                ? "opacity-50 line-through"
                                                : selectedIds.has(p.id)
                                                ? "bg-red-50"
                                                : ""
                                        }
                                    >
                                        <td>
                                            {p.status === "reserved" ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(p.id)}
                                                    onChange={() => toggleSelected(p.id)}
                                                    className="accent-red-700"
                                                    data-testid={`pallet-select-${p.pallet_number}`}
                                                    aria-label={`select pallet ${p.pallet_number}`}
                                                />
                                            ) : null}
                                        </td>
                                        <td className="odr-mono text-xs font-semibold">
                                            {p.pallet_number}
                                            {idx === 0 && (
                                                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-normal mt-1">
                                                    line: {lineId === "—" ? "—" : lineId.slice(0, 8)}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <StatusPill status={p.status} />
                                        </td>
                                        <td className="text-xs">
                                            {p.product_name_snapshot || "—"}
                                        </td>
                                        <td className="text-xs odr-mono">
                                            {p.rp_number || (
                                                <span className="text-gray-400">unlinked</span>
                                            )}
                                        </td>
                                        <td className="text-right odr-mono">
                                            {editingId === p.id ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    autoFocus
                                                    className="odr-input w-24 rounded-sm border border-gray-300 px-2 text-xs odr-mono text-right"
                                                    data-testid={`pallet-units-input-${p.pallet_number}`}
                                                />
                                            ) : (
                                                Number(p.units_per_pallet || 0).toLocaleString()
                                            )}
                                        </td>
                                        {isLtl && (
                                            <td className="text-xs odr-mono">
                                                {freightSummary(p)}
                                            </td>
                                        )}
                                        <td className="text-right">
                                            {editingId === p.id ? (
                                                <div className="inline-flex flex-col items-end gap-1">
                                                    <input
                                                        type="text"
                                                        value={editReason}
                                                        onChange={(e) => setEditReason(e.target.value)}
                                                        placeholder="reason (optional)"
                                                        className="odr-input w-40 rounded-sm border border-gray-300 px-2 text-[11px]"
                                                        data-testid={`pallet-units-reason-${p.pallet_number}`}
                                                    />
                                                    <div className="inline-flex gap-2">
                                                        <button
                                                            onClick={() => saveEdit(p)}
                                                            disabled={busyId === p.id}
                                                            className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                                                            data-testid={`pallet-units-save-${p.pallet_number}`}
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={cancelEdit}
                                                            disabled={busyId === p.id}
                                                            className="text-xs text-gray-500 hover:text-gray-700"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : voidId === p.id ? (
                                                <div className="inline-flex flex-col items-end gap-1">
                                                    <input
                                                        type="text"
                                                        value={voidReason}
                                                        onChange={(e) => setVoidReason(e.target.value)}
                                                        placeholder="void reason (≥3 chars)"
                                                        className="odr-input w-40 rounded-sm border border-gray-300 px-2 text-[11px]"
                                                        data-testid={`pallet-void-reason-${p.pallet_number}`}
                                                    />
                                                    <div className="inline-flex gap-2">
                                                        <button
                                                            onClick={() => submitVoid(p)}
                                                            disabled={busyId === p.id}
                                                            className="text-xs font-semibold text-red-700 hover:text-red-900"
                                                            data-testid={`pallet-void-confirm-${p.pallet_number}`}
                                                        >
                                                            Confirm
                                                        </button>
                                                        <button
                                                            onClick={cancelVoid}
                                                            disabled={busyId === p.id}
                                                            className="text-xs text-gray-500 hover:text-gray-700"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center gap-3 justify-end">
                                                    {p.status !== "voided" &&
                                                        p.status !== "shipped" && (
                                                            <button
                                                                onClick={() => startEdit(p)}
                                                                className="text-xs text-gray-700 hover:text-gray-900 inline-flex items-center gap-1"
                                                                data-testid={`pallet-edit-${p.pallet_number}`}
                                                                title="Edit unit count"
                                                            >
                                                                <Pencil className="w-3 h-3" />
                                                                Edit Qty
                                                            </button>
                                                        )}
                                                    {p.status !== "voided" && (
                                                        <button
                                                            onClick={() => reprint(p)}
                                                            disabled={busyId === p.id}
                                                            className="text-xs text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                                            data-testid={`pallet-reprint-${p.pallet_number}`}
                                                            title="Regenerate this pallet's Load Tag PDF with current data"
                                                        >
                                                            <Printer className="w-3 h-3" />
                                                            Reprint
                                                        </button>
                                                    )}
                                                    {isAdmin &&
                                                        p.status !== "voided" &&
                                                        p.status !== "shipped" && (
                                                            <button
                                                                onClick={() => startVoid(p)}
                                                                className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                                                                data-testid={`pallet-void-${p.pallet_number}`}
                                                                title="Void / cancel this unused pallet"
                                                            >
                                                                <Ban className="w-3 h-3" />
                                                                Void
                                                            </button>
                                                        )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )),
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {bulkOpen && (
                <BulkCreateRpModal
                    orderId={orderId}
                    selectedPallets={selectedReservedPallets}
                    onClose={() => setBulkOpen(false)}
                    onCreated={() => {
                        clearSelection();
                        refresh();
                    }}
                />
            )}
        </section>
    );
}
